import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OCCASIONS = {
  weddings: {
    label: 'Wedding Gifts',
    tagline: 'Whispered vows woven into every bead',
    description:
      "Witness bracelets, bridal party keepsakes, and his-and-hers sets — each a quiet ode to the day two became one.",
    image:
      'https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80',
  },
  'baby-showers': {
    label: 'Baby Showers',
    tagline: 'A whisper of love for new beginnings',
    description:
      "Soft, joyful Morse code bracelets to celebrate the arrival of someone tiny and adored.",
    image:
      'https://images.unsplash.com/photo-1519689680058-324335c77eba?w=1200&q=80',
  },
  'in-loving-memory': {
    label: 'In Loving Memory',
    tagline: 'Quiet keepsakes for hearts that remember',
    description:
      "Names, dates, and the words you wish you could still say — hidden in Morse code, worn close.",
    image:
      'https://images.unsplash.com/photo-1518895949257-7621c3c786d7?w=1200&q=80',
  },
  birthdays: {
    label: 'Birthdays',
    tagline: 'A personal gift, made one bead at a time',
    description:
      "Mark another trip around the sun with a bracelet that whispers their name, motto, or birth year.",
    image:
      'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=1200&q=80',
  },
  friends: {
    label: 'For Friends',
    tagline: 'Inside jokes and secret promises',
    description:
      "Matching sets and meaningful messages for the people who already speak your language without words.",
    image:
      'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=1200&q=80',
  },
  holidays: {
    label: 'Holidays',
    tagline: 'Thoughtful gifting, season after season',
    description:
      "Christmas, Mother's Day, Easter, Valentine's — a bracelet for the moments that come around each year.",
    image:
      'https://images.unsplash.com/photo-1576919228236-a097c32a5cd4?w=1200&q=80',
  },
};

export default function CollectionPage() {
  const { occasion } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const meta = OCCASIONS[occasion];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/products`, {
          params: { occasion },
        });
        // Pocket stones are PDP upsells — keep collection pages bracelet-only
        setProducts((response.data || []).filter((p) => p.category !== 'pocket-stone'));
      } catch (error) {
        logger.error('Error fetching collection:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [occasion]);

  if (!meta) {
    return (
      <div className="pt-28 pb-16 min-h-screen">
        <div className="container-custom px-6 md:px-12 lg:px-24 text-center">
          <h1 className="font-serif text-3xl text-[#354F52] mb-4">
            Collection not found
          </h1>
          <Link to="/products" className="btn-secondary">
            Shop All Bracelets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid={`collection-${occasion}`}>
      {/* Hero */}
      <section
        className="relative h-[55vh] min-h-[420px] flex items-center justify-center overflow-hidden"
        data-testid="collection-hero"
      >
        <img
          src={meta.image}
          alt={meta.label}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#354F52]/55" />
        <div className="relative z-10 text-center px-6 max-w-3xl">
          <p className="text-caption text-[#D4AF37] mb-3 tracking-[0.4em]">
            Shop by Occasion
          </p>
          <h1 className="heading-display text-4xl md:text-6xl text-[#FAF9F6] mb-4">
            {meta.label}
          </h1>
          <p className="font-serif italic text-lg md:text-xl text-[#CAD2C5] mb-6">
            {meta.tagline}
          </p>
          <p className="text-[#FAF9F6]/85 max-w-xl mx-auto leading-relaxed">
            {meta.description}
          </p>
        </div>
      </section>

      {/* Back link + grid */}
      <section className="section-padding">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors mb-12"
            data-testid="back-to-shop"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm uppercase tracking-widest">Shop All Bracelets</span>
          </Link>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-[#CAD2C5] rounded-sm" />
                  <div className="mt-4 h-6 bg-[#CAD2C5] rounded w-3/4 mx-auto" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[#52796F] mb-6">
                No bracelets tagged for this occasion just yet.
              </p>
              <Link to="/custom" className="btn-primary">
                Design a Custom Piece
              </Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#84A98C] mb-8" data-testid="collection-count">
                {products.length} {products.length === 1 ? 'bracelet' : 'bracelets'} for {meta.label.toLowerCase()}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
